import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HttpRequestMethod, send } from "./utils/send";
import { getUseDelete } from "./utils/useCrud/getUseDelete";

export type UserInvitation = {
  id: number;
  token: string;
  email: string;
  organization_id: number;
  created_by: number;
  created_at: string;
  expires_at: string;
  accepted_at?: string;
  accepted_by?: number;
  is_deleted: boolean;
};

export type UserInvitationValidation = {
  valid: boolean;
  organization_name?: string;
  invitation_email?: string;
};

const sendValidateUserInvitation = (token: string): Promise<UserInvitationValidation> =>
  send<UserInvitationValidation>(
    `/user-invitations/token/${token}`,
    HttpRequestMethod.GET,
    undefined,
    false
  );

const sendGetUserInvitations = (): Promise<UserInvitation[]> =>
  send<UserInvitation[]>("/user-invitations", HttpRequestMethod.GET);

const sendPostUserInvitation = (email: string): Promise<{ id: number }> =>
  send<{ id: number }>("/user-invitations", HttpRequestMethod.POST, { email });

const sendDeleteUserInvitation = (id: number): Promise<boolean> =>
  send<boolean>(`/user-invitations/${id}`, HttpRequestMethod.DELETE);

export const useUserInvitations = () => useQuery(["invitations"], sendGetUserInvitations);

export const useValidateUserInvitation = (token: string | null) =>
  useQuery(["invitations", "validate", token], () => sendValidateUserInvitation(token!), {
    enabled: !!token,
  });

export const useCreateUserInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => sendPostUserInvitation(email),
    onSuccess: () => queryClient.invalidateQueries(["invitations"]),
  });
};

export const useDeleteUserInvitation = getUseDelete(["invitations"], sendDeleteUserInvitation);
